/**
 * RoomContentSearchSection - 대화 내용으로 채팅방 검색 결과 섹션 (카카오톡 스타일)
 * 채팅방 목록 검색어 입력 시 이름 일치 결과 아래에 "대화 내용" 섹션으로 표시된다.
 * 내가 참여한 채팅방의 메시지만 검색되며, 방별로 최신 일치 메시지 1건 + 일치 건수를 보여준다.
 */

import { useQuery } from "@tanstack/react-query";
import { Loader2, MessageSquare, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import RoomProfileAvatar from "@/components/chat/RoomProfileAvatar";
import chatService, {
  type ChatRoomWithDetails,
  type RoomContentSearchResult,
} from "@/services/chatService";

interface RoomContentSearchSectionProps {
  /** 검색어 (원본, 내부에서 디바운스) */
  searchQuery: string;
  /** 내 채팅방 목록 (방 이름/아바타 표시용) */
  rooms: ChatRoomWithDetails[];
  currentUserId?: string;
  getRoomDisplayName: (room: ChatRoomWithDetails) => string;
  onRoomSelect: (roomId: number) => void;
  /** 채팅방 이름 일치 결과가 있는지 (없으면 이 섹션이 빈 검색 결과 안내를 담당) */
  hasNameMatches: boolean;
  isMobile?: boolean;
}

/** 일치 부분 앞뒤를 잘라 하이라이트된 스니펫을 만든다 */
function renderSnippet(content: string, query: string) {
  const normalized = content.replace(/\s+/g, " ").trim();
  const idx = normalized.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) {
    return <span>{normalized.slice(0, 80)}</span>;
  }
  const start = Math.max(0, idx - 15);
  const before = (start > 0 ? "…" : "") + normalized.slice(start, idx);
  const match = normalized.slice(idx, idx + query.length);
  const after = normalized.slice(idx + query.length, idx + query.length + 60);
  return (
    <span>
      {before}
      <span className="text-primary font-semibold">{match}</span>
      {after}
    </span>
  );
}

function formatResultTime(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return "어제";
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" });
  }
  return date.toLocaleDateString("ko-KR", { year: "2-digit", month: "numeric", day: "numeric" });
}

export default function RoomContentSearchSection({
  searchQuery,
  rooms,
  currentUserId,
  getRoomDisplayName,
  onRoomSelect,
  hasNameMatches,
  isMobile = false,
}: RoomContentSearchSectionProps) {
  const debouncedQuery = useDebounce(searchQuery.trim(), 300);
  const enabled = debouncedQuery.length >= 1;

  const { data: results = [], isLoading } = useQuery({
    queryKey: ["chat-room-content-search", debouncedQuery],
    queryFn: () => chatService.searchRoomsByContent(debouncedQuery),
    enabled,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });

  if (!enabled) return null;

  const roomMap = new Map(rooms.map((r) => [r.id, r]));
  const visibleResults = results
    .map((result) => {
      const room = roomMap.get(result.room_id);
      return room ? { result, room } : null;
    })
    .filter((v): v is { result: RoomContentSearchResult; room: ChatRoomWithDetails } => v !== null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-xs">대화 내용 검색 중...</span>
      </div>
    );
  }

  if (visibleResults.length === 0) {
    // 이름 일치도 없으면 여기서 통합 빈 결과 안내를 표시
    if (!hasNameMatches) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Search className="mx-auto h-8 w-8 mb-2" />
          <p className="text-sm">검색 결과가 없습니다</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-1.5 px-4 py-2 bg-muted/40 border-b">
        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">대화 내용</span>
      </div>
      {visibleResults.map(({ result, room }) => (
        <div
          key={`content-${result.room_id}`}
          onClick={() => onRoomSelect(room.id)}
          className={cn(
            "flex cursor-pointer items-center gap-3 border-b transition-colors select-none",
            "hover:bg-muted/50 active:bg-muted",
            isMobile ? "px-4 py-4 min-h-[72px]" : "px-3 py-3"
          )}
        >
          <RoomProfileAvatar room={room} currentUserId={currentUserId} compact={!isMobile} />
          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
              <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                <span className={cn("truncate font-medium", isMobile ? "text-base" : "text-sm")}>
                  {getRoomDisplayName(room)}
                </span>
                {room.room_type !== "direct" && (room.member_count || room.members?.length) ? (
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    ({room.member_count || room.members?.length})
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {result.match_count > 1 && (
                  <span className="text-[11px] text-primary font-medium">
                    {result.match_count}개
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formatResultTime(result.created_at)}
                </span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground truncate mt-0.5">
              {result.user_name && (
                <span className="text-xs mr-1">{result.user_name}:</span>
              )}
              {renderSnippet(result.content, debouncedQuery)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
